import { regulatoryWatchPaths } from '@/paths';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/table/DataTable';
import { TablePaginationStickyFooter } from '@/components/table/TablePaginationStickyFooter';
import { REGULATORY_WATCH_TABLE_PAGE_SIZE } from '@/components/table/constants';
import { CountryFilter } from '@/components/ui/country-select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexCol } from '@/components/ui/flex';
import { Progress } from '@/components/ui/progress';
import { Text } from '@/components/ui/text';
import { useDebounce } from '@/hooks/useDebounce';
import { useTracexIntegrationService } from '@/services/integrations';
import {
  useExportUpdatesService,
  useExportUpdatesToTracexService,
  useRegulatoryWatchService,
  useSourcesService,
} from '@/services/regulatoryWatch';
import { useCurrentWorkspaceService } from '@/services/workspaces';
import { ListUpdatesQuery, type ApplicabilityStatus } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type SortingState,
} from '@tanstack/react-table';
import {
  ChevronDown,
  Download,
  ExternalLink,
  Loader2,
  Plus,
} from 'lucide-react';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type FC,
  type ReactNode,
} from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { toast } from 'sonner';
import { cn } from '@/utils/classUtils';
import {
  LAST_UPDATED_PRESET_VALUES,
  type LastUpdatedPreset,
} from '../AllDocuments/LastUpdatedFilter';
import {
  AUDIT_TABLE_STATUS_OPTIONS,
  AUDIT_TABLE_VISIBLE_APPLICABILITY_STATUSES,
  PRIORITY_OPTIONS,
  SOURCE_TYPE_OPTIONS,
  STATUS_OPTIONS,
  UPDATE_TYPE_OPTIONS,
} from '../labels';
import { listUpdatesDateRangeFromPresetParams } from './datePresetBounds';
import { UpdatesDatePresetFilter } from './UpdatesDatePresetFilter';
import { UpdatesSearchField } from './UpdatesSearchField';
import { useUpdatesColumns } from './columns';
import {
  mergeRwPageSession,
  type RegulatoryWatchSessionPageId,
} from '../hooks/regulatoryWatchPageSession';
import {
  usePersistToRwSession,
  useRegulatoryWatchScrollSession,
} from '../hooks/useRegulatoryWatchPageSession';

const AUDIT_VISIBLE_STATUS_SET = new Set<string>(
  AUDIT_TABLE_VISIBLE_APPLICABILITY_STATUSES
);

const ALL_UPDATES_STATUS_OPTION_VALUES = new Set<string>(
  STATUS_OPTIONS.map(o => o.value)
);

const parseCsvParam = (value: string | null): string[] =>
  value
    ? value
        .split(',')
        .map(v => v.trim())
        .filter(Boolean)
    : [];

const OPTIONAL_FILTER_ORDER = [
  'type',
  'documentType',
  'geography',
  'source',
  'assignees',
  'effectiveDate',
] as const;

type OptionalFilterKey = (typeof OPTIONAL_FILTER_ORDER)[number];

const ADD_FILTER_LABELS: Record<OptionalFilterKey, string> = {
  type: 'Update type',
  documentType: 'Reference type',
  geography: 'Regulatory region',
  source: 'Source',
  assignees: 'Assigned to',
  effectiveDate: 'Effective',
};

function isValidPresetParam(v: string | null): v is LastUpdatedPreset {
  return (
    v !== null && (LAST_UPDATED_PRESET_VALUES as readonly string[]).includes(v)
  );
}

function MultiSelectActiveContent({
  options,
  selected,
}: {
  options: FilterOption[];
  selected: string[];
}): ReactNode {
  if (selected.length === 0) return null;
  if (selected.length === 1) {
    const o = options.find(x => x.value === selected[0]);
    return (
      <span className="inline-flex items-center gap-1.5 min-w-0 max-w-[220px]">
        {o?.renderPrefix}
        <span className="truncate">{o?.label}</span>
      </span>
    );
  }
  const iconCount = selected.length > 3 ? 3 : selected.length;
  return (
    <span className="inline-flex items-center gap-1 min-w-0">
      <span className="inline-flex items-center gap-0.5">
        {selected.slice(0, iconCount).map(val => {
          const o = options.find(x => x.value === val);
          if (o?.renderPrefix) {
            return (
              <span key={val} className="inline-flex shrink-0">
                {o.renderPrefix}
              </span>
            );
          }
          if (o?.icon) {
            return (
              <img
                key={val}
                src={o.icon}
                alt=""
                className="h-3.5 w-3.5 shrink-0 object-contain rounded-full"
              />
            );
          }
          return null;
        })}
      </span>
      <span className="tabular-nums">{selected.length} selected</span>
    </span>
  );
}

export const UpdatesTable = ({
  subscribedOnly,
  isBatchAssessing,
  assessmentProgress = 0,
  /** Audit table: only applicable / not_applicable by default. My updates view: every status, same table UI. */
  variant = 'audit',
  sessionPageId,
}: {
  subscribedOnly?: boolean;
  isBatchAssessing?: boolean;
  assessmentProgress?: number;
  variant?: 'audit' | 'allUpdates';
  sessionPageId: RegulatoryWatchSessionPageId;
}) => {
  const isAllUpdatesView = variant === 'allUpdates';
  const backInfo = isAllUpdatesView
    ? { path: regulatoryWatchPaths.allUpdates, label: 'My updates' }
    : { path: regulatoryWatchPaths.auditTable, label: 'Audit table' };
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceUname } = useParams<{ workspaceUname: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { sourceOptionsForFilter, geographyCodes, expandSourceFilter } =
    useSourcesService();
  const { currentWorkspace } = useCurrentWorkspaceService();
  const members = currentWorkspace?.members ?? [];
  const columns = useUpdatesColumns(workspaceUname ?? '', members);

  const { downloadExport, isExporting } = useExportUpdatesService();
  const { exportTracex, isExportingTracex } = useExportUpdatesToTracexService();
  const { integration: tracexIntegration } = useTracexIntegrationService();

  const searchQuery = searchParams.get('search') ?? '';
  const selectedTypes = parseCsvParam(searchParams.get('type'));
  const selectedDocumentTypes = parseCsvParam(searchParams.get('documentType'));
  const selectedSources = parseCsvParam(searchParams.get('source'));
  const selectedGeographies = parseCsvParam(searchParams.get('geography'));
  const selectedStatuses = parseCsvParam(searchParams.get('status'));
  const selectedPriorities = parseCsvParam(searchParams.get('priority'));
  const selectedAssignees = parseCsvParam(searchParams.get('assignees'));

  const publicationPresetParam = searchParams.get('publicationPreset');
  const publicationDateFrom = searchParams.get('publicationDateFrom') ?? '';
  const publicationDateTo = searchParams.get('publicationDateTo') ?? '';

  const effectivePresetParam = searchParams.get('effectivePreset');
  const effectiveDateFrom = searchParams.get('effectiveDateFrom') ?? '';
  const effectiveDateTo = searchParams.get('effectiveDateTo') ?? '';

  const committedPublicationPreset = useMemo((): LastUpdatedPreset => {
    if (isValidPresetParam(publicationPresetParam))
      return publicationPresetParam;
    if (publicationDateFrom && publicationDateTo) return 'custom';
    return 'none';
  }, [publicationPresetParam, publicationDateFrom, publicationDateTo]);

  const committedEffectivePreset = useMemo((): LastUpdatedPreset => {
    if (isValidPresetParam(effectivePresetParam)) return effectivePresetParam;
    if (effectiveDateFrom && effectiveDateTo) return 'custom';
    return 'none';
  }, [effectivePresetParam, effectiveDateFrom, effectiveDateTo]);

  const debouncedSearch = useDebounce(searchQuery, 300);

  const updateSearchParams = useCallback(
    (updater: (params: URLSearchParams) => void) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev);
          updater(next);
          // Sync session immediately so the route loader doesn't redirect back
          if (workspaceUname && sessionPageId) {
            mergeRwPageSession(workspaceUname, sessionPageId, {
              dirty: true,
              search: next.toString(),
            });
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, workspaceUname, sessionPageId]
  );

  const setSingleParam = useCallback(
    (key: string, value: string | undefined) => {
      updateSearchParams(params => {
        if (value) {
          params.set(key, value);
          return;
        }
        params.delete(key);
      });
    },
    [updateSearchParams]
  );

  const setCsvParam = useCallback(
    (key: string, values: string[]) => {
      setSingleParam(key, values.length > 0 ? values.join(',') : undefined);
    },
    [setSingleParam]
  );

  const applyPublicationPreset = useCallback(
    (next: { preset: LastUpdatedPreset; from?: string; to?: string }) => {
      updateSearchParams(p => {
        if (next.preset === 'none') {
          p.delete('publicationPreset');
          p.delete('publicationDateFrom');
          p.delete('publicationDateTo');
        } else if (next.preset === 'custom' && next.from && next.to) {
          p.set('publicationPreset', 'custom');
          p.set('publicationDateFrom', next.from);
          p.set('publicationDateTo', next.to);
        } else if (next.preset !== 'custom') {
          p.set('publicationPreset', next.preset);
          p.delete('publicationDateFrom');
          p.delete('publicationDateTo');
        }
      });
    },
    [updateSearchParams]
  );

  const applyEffectivePreset = useCallback(
    (next: { preset: LastUpdatedPreset; from?: string; to?: string }) => {
      updateSearchParams(p => {
        if (next.preset === 'none') {
          p.delete('effectivePreset');
          p.delete('effectiveDateFrom');
          p.delete('effectiveDateTo');
        } else if (next.preset === 'custom' && next.from && next.to) {
          p.set('effectivePreset', 'custom');
          p.set('effectiveDateFrom', next.from);
          p.set('effectiveDateTo', next.to);
        } else if (next.preset !== 'custom') {
          p.set('effectivePreset', next.preset);
          p.delete('effectiveDateFrom');
          p.delete('effectiveDateTo');
        }
      });
    },
    [updateSearchParams]
  );

  const publicationRangeIso = useMemo(
    () =>
      listUpdatesDateRangeFromPresetParams({
        preset: committedPublicationPreset,
        from: publicationDateFrom || undefined,
        to: publicationDateTo || undefined,
      }),
    [committedPublicationPreset, publicationDateFrom, publicationDateTo]
  );

  const effectiveRangeIso = useMemo(
    () =>
      listUpdatesDateRangeFromPresetParams({
        preset: committedEffectivePreset,
        from: effectiveDateFrom || undefined,
        to: effectiveDateTo || undefined,
      }),
    [committedEffectivePreset, effectiveDateFrom, effectiveDateTo]
  );

  const filters: ListUpdatesQuery = useMemo(() => {
    const q: ListUpdatesQuery = {};
    if (debouncedSearch.trim()) q.search = debouncedSearch.trim();
    if (selectedTypes.length > 0) q.type = selectedTypes.join(',');
    if (selectedDocumentTypes.length > 0)
      q.documentType = selectedDocumentTypes.join(',');
    if (selectedSources.length > 0) {
      const expandedSources = expandSourceFilter(selectedSources);
      q.source = expandedSources.join(',');
    }
    if (selectedGeographies.length > 0)
      q.geography = selectedGeographies.join(',');
    if (isAllUpdatesView) {
      const allowed = selectedStatuses.filter(s =>
        ALL_UPDATES_STATUS_OPTION_VALUES.has(s)
      );
      if (allowed.length > 0) {
        q.status = allowed.join(',');
      }
    } else {
      const allowedStatusSelections = selectedStatuses.filter(s =>
        AUDIT_VISIBLE_STATUS_SET.has(s)
      );
      q.status =
        allowedStatusSelections.length > 0
          ? allowedStatusSelections.join(',')
          : AUDIT_TABLE_VISIBLE_APPLICABILITY_STATUSES.join(',');
    }
    if (selectedPriorities.length > 0)
      q.priority = selectedPriorities.join(',');
    if (publicationRangeIso.from)
      q.publicationDateFrom = publicationRangeIso.from;
    if (publicationRangeIso.to) q.publicationDateTo = publicationRangeIso.to;
    if (effectiveRangeIso.from) q.effectiveDateFrom = effectiveRangeIso.from;
    if (effectiveRangeIso.to) q.effectiveDateTo = effectiveRangeIso.to;
    if (selectedAssignees.length > 0) q.assignees = selectedAssignees.join(',');
    if (subscribedOnly) q.subscribedOnly = 'true';
    return q;
  }, [
    debouncedSearch,
    selectedTypes,
    selectedDocumentTypes,
    selectedSources,
    expandSourceFilter,
    selectedGeographies,
    selectedStatuses,
    selectedPriorities,
    selectedAssignees,
    publicationRangeIso,
    effectiveRangeIso,
    subscribedOnly,
    isAllUpdatesView,
  ]);

  const {
    updates: allUpdates,
    isPending,
    error,
  } = useRegulatoryWatchService(filters, {
    refetchInterval: 3000,
  });

  const showingProgressiveRows = isBatchAssessing && assessmentProgress >= 20;
  const updates = useMemo(() => {
    const base = showingProgressiveRows
      ? allUpdates.filter(u => u.context?.aiImpactStatus !== 'generating')
      : allUpdates;
    if (isAllUpdatesView) {
      return base;
    }
    return base.filter(u => {
      const status = (u.context?.status ?? 'to_review') as ApplicabilityStatus;
      return AUDIT_VISIBLE_STATUS_SET.has(status);
    });
  }, [allUpdates, showingProgressiveRows, isAllUpdatesView]);

  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: REGULATORY_WATCH_TABLE_PAGE_SIZE,
  });

  const [sorting, setSorting] = useState<SortingState>([
    { id: 'publicationDate', desc: true },
  ]);

  const tableState = useMemo(
    () => ({ pagination, sorting }),
    [pagination, sorting]
  );
  usePersistToRwSession(sessionPageId, tableState, s => ({
    pagination: s.pagination,
    sorting: s.sorting,
  }));

  const tableScrollCaptureRef = useRef<HTMLDivElement>(null);
  const tableReadyForScroll =
    !(isBatchAssessing && assessmentProgress < 20) && !isPending;
  useRegulatoryWatchScrollSession(
    sessionPageId,
    tableScrollCaptureRef,
    tableReadyForScroll
  );

  // Reset page when query filters change.
  useEffect(() => {
    setPagination(p => ({ ...p, pageIndex: 0 }));
  }, [
    debouncedSearch,
    selectedTypes.join(','),
    selectedDocumentTypes.join(','),
    selectedSources.join(','),
    selectedGeographies.join(','),
    selectedStatuses.join(','),
    selectedPriorities.join(','),
    selectedAssignees.join(','),
    publicationRangeIso.from,
    publicationRangeIso.to,
    effectiveRangeIso.from,
    effectiveRangeIso.to,
    subscribedOnly,
    isAllUpdatesView,
  ]);

  const optionalKeysFromUrl = useMemo(() => {
    const keys: OptionalFilterKey[] = [];
    if (selectedTypes.length > 0) keys.push('type');
    if (selectedDocumentTypes.length > 0) keys.push('documentType');
    if (selectedGeographies.length > 0) keys.push('geography');
    if (selectedSources.length > 0) keys.push('source');
    if (selectedAssignees.length > 0) keys.push('assignees');
    if (committedEffectivePreset !== 'none') keys.push('effectiveDate');
    return keys;
  }, [
    selectedTypes.length,
    selectedDocumentTypes.length,
    selectedGeographies.length,
    selectedSources.length,
    selectedAssignees.length,
    committedEffectivePreset,
  ]);

  const [addedOptionalKeys, setAddedOptionalKeys] = useState<
    OptionalFilterKey[]
  >([]);

  const visibleOptionalKeys = useMemo(
    () =>
      OPTIONAL_FILTER_ORDER.filter(
        k => optionalKeysFromUrl.includes(k) || addedOptionalKeys.includes(k)
      ),
    [optionalKeysFromUrl, addedOptionalKeys]
  );

  const addOptionalFilter = useCallback((key: OptionalFilterKey) => {
    setAddedOptionalKeys(prev => (prev.includes(key) ? prev : [...prev, key]));
  }, []);

  const handleOptionalCsv = useCallback(
    (key: OptionalFilterKey, values: string[]) => {
      const paramKey =
        key === 'type'
          ? 'type'
          : key === 'documentType'
            ? 'documentType'
            : key === 'geography'
              ? 'geography'
              : key === 'source'
                ? 'source'
                : 'assignees';
      setCsvParam(paramKey, values);
      if (values.length === 0) {
        setAddedOptionalKeys(prev => prev.filter(k => k !== key));
      }
    },
    [setCsvParam]
  );

  const statusFilterOptions = isAllUpdatesView
    ? STATUS_OPTIONS
    : AUDIT_TABLE_STATUS_OPTIONS;
  const statusSelectionSet = isAllUpdatesView
    ? ALL_UPDATES_STATUS_OPTION_VALUES
    : AUDIT_VISIBLE_STATUS_SET;

  const hasAnyFilterActive =
    selectedPriorities.length > 0 ||
    selectedStatuses.length > 0 ||
    selectedTypes.length > 0 ||
    selectedDocumentTypes.length > 0 ||
    selectedGeographies.length > 0 ||
    selectedSources.length > 0 ||
    selectedAssignees.length > 0 ||
    committedPublicationPreset !== 'none' ||
    committedEffectivePreset !== 'none';

  const clearAllFilters = useCallback(() => {
    updateSearchParams(p => {
      [
        'priority',
        'status',
        'type',
        'documentType',
        'geography',
        'source',
        'assignees',
        'publicationPreset',
        'publicationDateFrom',
        'publicationDateTo',
        'effectivePreset',
        'effectiveDateFrom',
        'effectiveDateTo',
      ].forEach(k => p.delete(k));
    });
    setAddedOptionalKeys([]);
  }, [updateSearchParams]);

  const addFilterChoices = useMemo(
    () => OPTIONAL_FILTER_ORDER.filter(k => !visibleOptionalKeys.includes(k)),
    [visibleOptionalKeys]
  );

  if (error) {
    return (
      <Text variant="body-sm" color="primary">
        An error occurred while loading updates.
      </Text>
    );
  }

  return (
    <div className="flex flex-col min-w-0 min-h-0 flex-1">
      <div className="shrink-0 flex flex-col gap-4 px-6 py-4 border-b border-neutral-200">
        <div className="flex w-full min-h-9 flex-row flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <UpdatesSearchField
            value={searchQuery}
            onChange={v => setSingleParam('search', v || undefined)}
          />
          <div className="flex shrink-0 items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={
                isExporting || isExportingTracex || updates.length === 0
              }
              onClick={async () => {
                try {
                  await downloadExport(filters);
                  toast.success('Export started');
                } catch {
                  toast.error('Failed to export updates');
                }
              }}
            >
              {isExporting ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Download className="size-3.5" />
              )}
              {isExporting ? 'Exporting...' : 'Export to Excel'}
            </Button>
            {tracexIntegration?.status === 'enabled' && (
              <Button
                variant="outline"
                size="sm"
                disabled={
                  isExporting || isExportingTracex || updates.length === 0
                }
                onClick={async () => {
                  try {
                    const result = await exportTracex(filters);
                    if (result.errors.length > 0) {
                      toast.warning(
                        `Exported ${result.exportedCount} updates to TraceX with ${result.errors.length} errors`
                      );
                    } else {
                      toast.success(
                        `Exported ${result.exportedCount} updates to TraceX`
                      );
                    }
                  } catch {
                    toast.error('Failed to export updates to TraceX');
                  }
                }}
              >
                {isExportingTracex ? (
                  <Loader2 className="size-3.5 animate-spin" />
                ) : (
                  <ExternalLink className="size-3.5" />
                )}
                {isExportingTracex ? 'Exporting...' : 'Export to TraceX'}
              </Button>
            )}
          </div>
        </div>

        <div className="flex flex-wrap items-start gap-2 w-full">
          <div className="flex flex-wrap items-center gap-2 flex-1 min-w-0">
            <OptionFilter
              label="Priority"
              options={PRIORITY_OPTIONS}
              selected={selectedPriorities}
              onChange={selected => setCsvParam('priority', selected)}
            />
            <OptionFilter
              label="Status"
              options={statusFilterOptions}
              selected={selectedStatuses.filter(s => statusSelectionSet.has(s))}
              onChange={selected => setCsvParam('status', selected)}
            />
            <UpdatesDatePresetFilter
              label="Published"
              committedPreset={committedPublicationPreset}
              committedFrom={publicationDateFrom}
              committedTo={publicationDateTo}
              onApply={applyPublicationPreset}
              onClearCommitted={() =>
                applyPublicationPreset({ preset: 'none' })
              }
              size="sm"
              filterPopoverProps={{
                activeAppearance: 'outline',
                operatorClassName: 'text-muted-foreground',
              }}
            />

            {visibleOptionalKeys.map(key => {
              if (key === 'type') {
                return (
                  <OptionFilter
                    key={key}
                    label="Update type"
                    options={UPDATE_TYPE_OPTIONS}
                    selected={selectedTypes}
                    onChange={v => handleOptionalCsv('type', v)}
                  />
                );
              }
              if (key === 'documentType') {
                return (
                  <OptionFilter
                    key={key}
                    label="Reference type"
                    options={SOURCE_TYPE_OPTIONS}
                    selected={selectedDocumentTypes}
                    onChange={v => handleOptionalCsv('documentType', v)}
                  />
                );
              }
              if (key === 'geography') {
                return (
                  <CountryFilter
                    key={key}
                    label="Regulatory region"
                    countries={geographyCodes}
                    selected={selectedGeographies}
                    onChange={v => handleOptionalCsv('geography', v)}
                    size="sm"
                    filterPopoverProps={{
                      activeAppearance: 'outline',
                      operatorClassName: 'text-muted-foreground',
                    }}
                  />
                );
              }
              if (key === 'source') {
                return (
                  <OptionFilter
                    key={key}
                    label="Source"
                    options={sourceOptionsForFilter}
                    selected={selectedSources}
                    onChange={v => handleOptionalCsv('source', v)}
                  />
                );
              }
              if (key === 'assignees') {
                return (
                  <OptionFilter
                    key={key}
                    label="Assigned to"
                    options={(members ?? []).map(m => ({
                      label:
                        `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() ||
                        m.email,
                      value: m.id,
                    }))}
                    selected={selectedAssignees}
                    onChange={v => handleOptionalCsv('assignees', v)}
                  />
                );
              }
              if (key === 'effectiveDate') {
                return (
                  <UpdatesDatePresetFilter
                    key={key}
                    label="Effective"
                    committedPreset={committedEffectivePreset}
                    committedFrom={effectiveDateFrom}
                    committedTo={effectiveDateTo}
                    onApply={applyEffectivePreset}
                    onClearCommitted={() => {
                      applyEffectivePreset({ preset: 'none' });
                      setAddedOptionalKeys(prev =>
                        prev.filter(k => k !== 'effectiveDate')
                      );
                    }}
                    size="sm"
                    filterPopoverProps={{
                      activeAppearance: 'outline',
                      operatorClassName: 'text-muted-foreground',
                    }}
                  />
                );
              }
              return null;
            })}

            {addFilterChoices.length > 0 && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className={cn(
                      'inline-flex items-center gap-1 rounded-[100px] border border-input bg-background px-2.5 py-1 text-xs font-medium shadow-sm',
                      'text-foreground hover:bg-accent transition-colors',
                      'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                    )}
                  >
                    <Plus className="h-3 w-3 shrink-0" />
                    Add filter
                    <ChevronDown className="h-3 w-3 shrink-0" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="min-w-[200px]">
                  {addFilterChoices.map(key => (
                    <DropdownMenuItem
                      key={key}
                      onSelect={() => addOptionalFilter(key)}
                    >
                      {ADD_FILTER_LABELS[key]}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {hasAnyFilterActive && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 text-xs text-muted-foreground hover:text-foreground"
              onClick={clearAllFilters}
            >
              Clear
            </Button>
          )}
        </div>
      </div>

      {/* Table + sticky footer (footer outside scroll area) */}
      <div className="relative flex min-h-0 min-w-0 flex-1 flex-col">
        {isBatchAssessing && assessmentProgress < 20 ? (
          <div className="relative min-h-0 flex-1 overflow-auto px-6">
            <BatchAssessingPlaceholder progress={assessmentProgress} />
          </div>
        ) : (
          <>
            <div className="relative min-h-0 flex-1 overflow-auto px-6">
              {isBatchAssessing && assessmentProgress >= 20 && (
                <div className="sticky top-0 z-10">
                  <Progress
                    value={assessmentProgress}
                    className="h-1 rounded-none bg-tan-400"
                    color="yellow"
                  />
                </div>
              )}
              <DataTable
                columns={columns}
                data={updates}
                isLoading={isPending}
                hidePagination
                scrollContainerRef={tableScrollCaptureRef}
                onRowClick={row => {
                  const index = updates.findIndex(u => u.id === row.id);
                  navigate(
                    `/workspace/${workspaceUname}/regulatory-watch/updates/${row.id}${location.search}`,
                    {
                      state: {
                        updateIds: updates.map(u => u.id),
                        currentIndex: index >= 0 ? index : 0,
                        backPath: `/workspace/${workspaceUname}/regulatory-watch/${backInfo.path}`,
                        backLabel: backInfo.label,
                      },
                    }
                  );
                }}
                enableColumnResizing
                columnResizeMode="onChange"
                grid="bordered"
                scrollX
                options={{
                  getPaginationRowModel: getPaginationRowModel(),
                  getSortedRowModel: getSortedRowModel(),
                  onPaginationChange: setPagination,
                  onSortingChange: setSorting,
                  enableMultiSort: false,
                  getRowId: row => row.id,
                  autoResetPageIndex: false,
                  initialState: {
                    columnPinning: {
                      left: ['priority', 'status', 'update'],
                      right: [],
                    },
                  },
                  state: {
                    pagination,
                    sorting,
                  },
                }}
              />
            </div>
            {!isPending && updates.length > 0 && (
              <TablePaginationStickyFooter
                totalCount={updates.length}
                pageIndex={pagination.pageIndex}
                pageSize={pagination.pageSize}
                onPageIndexChange={pageIndex =>
                  setPagination(p => ({ ...p, pageIndex }))
                }
                entityLabel="updates"
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

const OptionFilter: FC<{
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const isActive = selected.length > 0;

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={
        isActive ? (
          <MultiSelectActiveContent options={options} selected={selected} />
        ) : undefined
      }
      onClear={isActive ? () => onChange([]) : undefined}
      size="sm"
      activeAppearance="outline"
      operatorClassName="text-muted-foreground"
    >
      <FilterOptionList
        options={options}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};

const CYCLING_MESSAGES = [
  'Fetching the latest updates from 4,200+ global sources\u2026',
  'Filtering for what\u2019s relevant to you\u2026',
  'Generating custom impact assessments and priorities\u2026',
];

const CYCLE_INTERVAL_MS = 3000;

const BatchAssessingPlaceholder: FC<{ progress: number }> = ({ progress }) => {
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveIndex(prev => (prev + 1) % CYCLING_MESSAGES.length);
    }, CYCLE_INTERVAL_MS);
    return () => clearInterval(interval);
  }, []);

  return (
    <>
      <div className="sticky top-0 z-10">
        <Progress
          value={progress}
          className="h-1 rounded-none bg-tan-400"
          color="yellow"
        />
      </div>
      <FlexCol
        gap="4"
        alignItems="center"
        justifyContent="center"
        className="pt-40"
      >
        <Text variant="body" className="font-medium text-neutral-900">
          Generating your personalized feed
        </Text>
        <div className="flex items-center gap-2">
          <Loader2 className="h-4 w-4 text-neutral-400 animate-spin" />
          <Text
            variant="body-sm"
            color="secondary"
            className="transition-opacity duration-300"
          >
            {CYCLING_MESSAGES[activeIndex]}
          </Text>
        </div>
      </FlexCol>
    </>
  );
};
